import React, { useState } from "react";
import { Modal, Button, Row, Typography, Flex, notification } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { useEditScheduledStreamMutation, useEditWorkoutMutation, useGetDomainGymQuery } from "store/api/api";
import usePrimaryColor from "helpers/hooks/usePrimaryColor";
import "./styles.scss";
import alpha from "helpers/alpha";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectSelectedWorkout } from "store/selectors";
import LiveStreamIcon from "images/svgIcons/live-stream";
import timezone from "dayjs/plugin/timezone";
import dayjs from "dayjs";
dayjs.extend(timezone);

const GoLive = ({ open, closeModal, refetchStreams }) => {
	const [currentStep, setCurrentStep] = useState(1);
	const primaryColor = usePrimaryColor();
	const [editScheduledStreamTrigger, editStream] = useEditScheduledStreamMutation();
	const [editWorkoutTrigger, editWorkout] = useEditWorkoutMutation();
	const { gym } = useParams();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const domainGymId = domainGymResponse.data?.id;
	const liveStream = useSelector(selectSelectedWorkout);

	const handleCancel = () => {
		closeModal();
		setCurrentStep(1);
	};

	const handleNext = () => {
		setCurrentStep(2);
	};

	const handleGoLive = async () => {
		if (liveStream?.upload_id) {
			editWorkoutTrigger({
				workout_id: liveStream.id,
				gym_id: domainGymId,
				upload_id: liveStream?.mux_stream_key,
			});
		}
		await editScheduledStreamTrigger({
			workout_id: liveStream.workout_id,
			gym_id: liveStream.gym_id,
			start_time: liveStream.start_time,
			time_zone: liveStream.time_zone,
			currently_streaming: 1,
		}).unwrap();
		closeModal();
		refetchStreams();
		setCurrentStep(1);
	};

	return (
		<Modal destroyOnClose className="need-help" open={open} onCancel={handleCancel} footer={null}>
			<Typography.Title style={{ textAlign: "center", color: "#000", height: 40 }} level={3}>
				Start Streaming
			</Typography.Title>
			{currentStep === 1 && (
				<Typography.Paragraph style={{ textAlign: "center", fontSize: 12, fontWeight: 400 }}>
					Enter the stream settings below in your streaming software to get connected.
				</Typography.Paragraph>
			)}
			{currentStep === 2 && (
				<Flex justify="center">
					<Flex
						justify="center"
						gap={8}
						align="center"
						style={{
							backgroundColor: "var(--background-color)",
							padding: "8px 16px",
							borderRadius: 5,
						}}
					>
						<LiveStreamIcon height={16} width={16} />
						<Typography.Text style={{ color: "#000", fontSize: 16, fontWeight: 500 }}>
							{dayjs.tz(liveStream.start_time, liveStream.time_zone).local().isAfter(dayjs()) &&
							dayjs.tz(liveStream.start_time, liveStream.time_zone).local().isBefore(dayjs())
								? `was scheduled to start ${dayjs
										.tz(liveStream.start_time, liveStream.time_zone)
										.local()
										.toNow(true)} ago`
								: ""}
							{`Live in ${dayjs.tz(liveStream.start_time, liveStream.time_zone).local().toNow(true)} (Scheduled Time)`}
						</Typography.Text>
					</Flex>
				</Flex>
			)}
			<Flex className="steps">
				<div className={`${currentStep === 1 && "active"}`} />
				<div className={`${currentStep === 2 && "active"}`} />
			</Flex>

			{currentStep === 1 && (
				<>
					<Typography.Text style={{ color: "#000", fontWeight: 400 }}>Broadcast Server</Typography.Text>
					<Flex justify="space-between" className="stream-settings">
						<Typography.Text style={{ color: "#000", fontWeight: 600 }}>
							rtmp://global-live.mux.com:5222/app
						</Typography.Text>
						<Typography.Text
							copyable={{
								text: "rtmp://global-live.mux.com:5222/app",
								icon: <CopyOutlined style={{ cursor: "pointer", color: primaryColor }} />,
								onCopy: () => notification.success({ message: "Copied to clipboard" }),
							}}
							style={{
								color: primaryColor,
								backgroundColor: alpha(primaryColor, 0.1),
								borderRadius: "14px",
								padding: "0 4px",
							}}
						>
							Copy
						</Typography.Text>
					</Flex>
					<Typography.Text style={{ color: "#000", fontWeight: 400 }}>Stream Key</Typography.Text>
					<Flex justify="space-between" className="stream-settings">
						<Typography.Text ellipsis style={{ color: "#000", width: "80%", fontWeight: 600 }}>
							{liveStream?.mux_stream_key}
						</Typography.Text>
						<Typography.Text
							copyable={{
								text: liveStream?.mux_stream_key,
								icon: <CopyOutlined style={{ cursor: "pointer", color: primaryColor }} />,
								onCopy: () => notification.success({ message: "Copied to clipboard" }),
							}}
							style={{
								color: primaryColor,
								backgroundColor: alpha(primaryColor, 0.1),
								borderRadius: "14px",
								padding: "0 4px",
							}}
						>
							Copy
						</Typography.Text>
					</Flex>
					<Row className="label">Step 1 - Find Stream Settings</Row>
					<Typography.Paragraph style={{ fontSize: 12 }}>
						1. Click <strong style={{ fontWeight: 700 }}>“Settings”</strong> on the bottom right hand corner.
					</Typography.Paragraph>
					<Typography.Text style={{ fontSize: 12 }}>
						2. From settings window click on <strong style={{ fontWeight: 700 }}>“Stream”</strong> located on the
						sidebar.
					</Typography.Text>
					<Row className="label">Step 2 - Enter Broadcast Server Link and Stream Key</Row>
					<Button type="primary" onClick={handleNext} style={{ marginTop: "16px", width: "100%" }}>
						Next
					</Button>
				</>
			)}
			{currentStep === 2 && (
				<>
					<Row className="label">Click on Go Live when you are ready</Row>
					<Typography.Text style={{ color: "#000", fontWeight: 400 }}>Broadcast Server</Typography.Text>
					<Flex justify="space-between" className="stream-settings">
						<Typography.Text style={{ color: "#000", fontWeight: 600 }}>
							rtmp://global-live.mux.com:5222/app
						</Typography.Text>
						<Typography.Text
							copyable={{
								text: "rtmp://global-live.mux.com:5222/app",
								icon: <CopyOutlined style={{ cursor: "pointer", color: primaryColor }} />,
								onCopy: () => notification.success({ message: "Copied to clipboard" }),
							}}
							style={{
								color: primaryColor,
								backgroundColor: alpha(primaryColor, 0.1),
								borderRadius: "14px",
								padding: "0 8px",
							}}
						>
							Copy
						</Typography.Text>
					</Flex>
					<Typography.Text style={{ color: "#000", fontWeight: 400 }}>Stream Key</Typography.Text>
					<Flex justify="space-between" className="stream-settings">
						<Typography.Text ellipsis style={{ color: "#000", fontWeight: 600 }}>
							{liveStream?.mux_stream_key}
						</Typography.Text>
						<Typography.Text
							copyable={{
								text: liveStream?.mux_stream_key,
								icon: <CopyOutlined style={{ cursor: "pointer", color: primaryColor }} />,
								onCopy: () => notification.success({ message: "Copied to clipboard" }),
							}}
							ellipsis
							style={{
								color: primaryColor,
								backgroundColor: alpha(primaryColor, 0.1),
								borderRadius: "14px",
								padding: "0 8px",
							}}
						>
							Copy
						</Typography.Text>
					</Flex>
					<Flex justify="space-between" gap={16}>
						<Button size="small" type="primary" onClick={() => setCurrentStep(1)} style={{ flex: 1 }}>
							Back
						</Button>
						<Button
							size="small"
							onClick={handleGoLive}
							style={{ flex: 1, backgroundColor: primaryColor, color: "#fff", border: "none" }}
							loading={editStream.isLoading || editWorkout.isLoading}
						>
							<Flex gap={8} style={{ fontSize: 16, color: "#fff" }} align="center">
								<LiveStreamIcon height={16} width={16} />
								Go Live
							</Flex>
						</Button>
					</Flex>
				</>
			)}
		</Modal>
	);
};

export default GoLive;

